import { useEffect, useState } from "react";
import axios from "axios";
import Swal from 'sweetalert2';
import { getCookies } from "cookies-next";
import { config } from "../../data/config";
import { useTranslation } from 'react-i18next';
import BreadCrump from "../tools/breadcrump";
import HeaderOfPage from "../tools/headerOfPage";

interface AffiliateSettingsData {
    commissionPercentage: number;
    minPayoutAmount: number;
    cookieExpiryDays: number;
    enabled: boolean;
}

export default function AffiliateSettingsPage() {
    const { t } = useTranslation();
    const cookie = getCookies();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [settingsId, setSettingsId] = useState<string>('');
    const [settings, setSettings] = useState<AffiliateSettingsData>({
        commissionPercentage: 10,
        minPayoutAmount: 50,
        cookieExpiryDays: 30,
        enabled: true
    });

    const breadcrump = [
        { name: t('Dashboard'), url: '/dashboard' },
        { name: t('Setting'), url: '/dashboard/Setting' },
        { name: t('Affiliate Settings'), url: null },
    ];

    const loadSettings = async () => {
        try {
            const res = await axios({
                method: 'get',
                url: `${config.url}/v1/dashboard/setting`,
                headers: {
                    Authorization: 'Bearer ' + cookie['token'],
                }
            });

            if (res.data.message) {
                setSettingsId(res.data.message._id);
                if (res.data.message.affiliateSettings) {
                    setSettings(res.data.message.affiliateSettings);
                }
            }
        } catch (error: any) {
            console.error('Error loading settings:', error);
            Swal.fire({
                icon: 'error',
                title: t('Error'),
                text: t('Failed to load settings'),
            });
        } finally {
            setLoading(false);
        }
    };

    const saveSettings = async () => {
        if (!settingsId) {
            Swal.fire({
                icon: 'error',
                title: t('Error'),
                text: t('Failed to load settings'),
            });
            return;
        }

        setSaving(true);
        try {
            await axios({
                method: 'put',
                url: `${config.url}/v1/dashboard/setting/${settingsId}`,
                data: {
                    affiliateSettings: settings
                },
                headers: {
                    Authorization: 'Bearer ' + cookie['token'],
                }
            });

            Swal.fire({
                icon: 'success',
                title: t('Success'),
                text: t('Affiliate settings saved successfully'),
                timer: 2000,
                showConfirmButton: false
            });
        } catch (error: any) {
            console.error('Error saving settings:', error);
            Swal.fire({
                icon: 'error',
                title: t('Error'),
                text: error.response?.data?.message || t('Failed to save settings'),
            });
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        loadSettings();
    }, []);

    if (loading) {
        return (
            <div className="bg-gray-50 min-h-screen p-4 md:p-6">
                <BreadCrump data={breadcrump} />
                <HeaderOfPage name={t('Affiliate Settings')} />
                <div className="mt-6 bg-white rounded-xl shadow p-8 text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">{t('Loading settings...')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gray-50 min-h-screen p-4 md:p-6">
            <BreadCrump data={breadcrump} />
            <HeaderOfPage name={t('Affiliate Settings')} />

            <div className="mt-6 space-y-6">
                {/* Enable/Disable Toggle */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-800">{t('Affiliate Program')}</h3>
                            <p className="text-sm text-gray-500 mt-1">{t('Enable or disable the affiliate marketing program')}</p>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className={`text-sm font-medium ${!settings.enabled ? 'text-gray-900' : 'text-gray-400'}`}>
                                {t('Disabled')}
                            </span>
                            <button
                                type="button"
                                role="switch"
                                aria-checked={settings.enabled}
                                onClick={() => setSettings({ ...settings, enabled: !settings.enabled })}
                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${settings.enabled ? 'bg-blue-600' : 'bg-gray-200'}`}
                            >
                                <span
                                    aria-hidden="true"
                                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${settings.enabled ? 'translate-x-5' : 'translate-x-0'}`}
                                />
                            </button>
                            <span className={`text-sm font-medium ${settings.enabled ? 'text-gray-900' : 'text-gray-400'}`}>
                                {t('Enabled')}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Commission Settings */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-6">{t('Commission Settings')}</h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Commission Percentage */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('Commission Percentage')}
                            </label>
                            <div className="relative">
                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.5"
                                    value={settings.commissionPercentage}
                                    onChange={(e) => setSettings({ ...settings, commissionPercentage: parseFloat(e.target.value) || 0 })}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none pr-12"
                                    placeholder="10"
                                />
                                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                            </div>
                            <p className="mt-2 text-sm text-gray-500">
                                {t('Percentage of order total paid to affiliates')}
                            </p>
                        </div>

                        {/* Minimum Payout Amount */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('Minimum Payout Amount')}
                            </label>
                            <div className="relative">
                                <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={settings.minPayoutAmount}
                                    onChange={(e) => setSettings({ ...settings, minPayoutAmount: parseFloat(e.target.value) || 0 })}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none pr-12"
                                    placeholder="50"
                                />
                                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">€</span>
                            </div>
                            <p className="mt-2 text-sm text-gray-500">
                                {t('Minimum earned amount before payout is allowed')}
                            </p>
                        </div>

                        {/* Cookie Expiry Days */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('Referral Cookie Duration')}
                            </label>
                            <div className="relative">
                                <input
                                    type="number"
                                    min="1"
                                    max="365"
                                    step="1"
                                    value={settings.cookieExpiryDays}
                                    onChange={(e) => setSettings({ ...settings, cookieExpiryDays: parseInt(e.target.value) || 30 })}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none pr-16"
                                    placeholder="30"
                                />
                                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">{t('days')}</span>
                            </div>
                            <p className="mt-2 text-sm text-gray-500">
                                {t("How long the referral code is saved in buyer's browser")}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Preview Card */}
                <div className="bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl shadow-sm p-6 text-white">
                    <h3 className="text-lg font-semibold mb-4">{t('Current Configuration Preview')}</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-white/20 rounded-lg p-4">
                            <p className="text-sm opacity-80">{t('Status')}</p>
                            <p className="text-xl font-bold">{settings.enabled ? `✓ ${t('Active')}` : `✗ ${t('Disabled')}`}</p>
                        </div>
                        <div className="bg-white/20 rounded-lg p-4">
                            <p className="text-sm opacity-80">{t('Commission Percentage')}</p>
                            <p className="text-xl font-bold">{settings.commissionPercentage}%</p>
                        </div>
                        <div className="bg-white/20 rounded-lg p-4">
                            <p className="text-sm opacity-80">{t('Minimum Payout Amount')}</p>
                            <p className="text-xl font-bold">€{settings.minPayoutAmount}</p>
                        </div>
                        <div className="bg-white/20 rounded-lg p-4">
                            <p className="text-sm opacity-80">{t('Referral Cookie Duration')}</p>
                            <p className="text-xl font-bold">{settings.cookieExpiryDays} {t('days')}</p>
                        </div>
                    </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end">
                    <button
                        onClick={saveSettings}
                        disabled={saving}
                        className="px-8 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {saving ? (
                            <>
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                {t('Saving...')}
                            </>
                        ) : (
                            <>
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                {t('Save Settings')}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}
